import { toast } from 'sonner';
import { storeLogger, EVENTS } from './logging';

type UnknownRecord = Record<string, unknown>;

/**
 * Batch actions for multi-connection store
 * - fetchAccountsForAllConnections
 * - shared throttling / de-dupe logic
 */
export const createBatchActions = (set: unknown, get: unknown) => {
  const _set = set as (partial: Partial<UnknownRecord> | ((state: UnknownRecord) => Partial<UnknownRecord>)) => void;
  const _get = get as () => UnknownRecord;
  return ({
  // === FETCH ACCOUNTS FOR ALL CONNECTED INSTITUTIONS ===
  fetchAccountsForAllConnections: async (isNavigation = false, forceRefreshInstitutionId?: string) => {
    const state = _get();

    // DEDUPLICATION: Check if already fetching all connections
    if (state.isFetchingAllConnections) {
      return;
    }

    // DEDUPLICATION: Check if recent batch fetch occurred (within 30 seconds)
    const rawLast = state.lastBatchFetch as unknown;
    const lastDate = rawLast instanceof Date ? rawLast : (rawLast ? new Date(String(rawLast)) : null);
    const batchCacheValid = !!lastDate && (Date.now() - lastDate.getTime()) < 30 * 1000; // 30 seconds

    if (!forceRefreshInstitutionId && batchCacheValid) {
      return;
    }

    const connMap = (state.connections as Map<string, unknown>) || new Map();
    const allConnections = Array.from(connMap.values());
    // Allow batch fetching for connected and eligible statuses (idle/pending/loading_accounts)
    const connections = allConnections.filter((c) => {
      const rec = c as UnknownRecord;
      const isConnected = Boolean(rec['isConnected']);
      const status = String(rec['status'] || '');
      return isConnected || status === 'idle' || status === 'pending' || status === 'loading_accounts';
    });

    if (connections.length === 0) {
      return;
    }

    // Set batch fetching flag
    _set({ isFetchingAllConnections: true });
    // Log batch start
    storeLogger.info('multiWealthica2Store', EVENTS.BATCH_FETCH_START, {
      totalConnections: connections.length,
      forceRefreshInstitutionId: forceRefreshInstitutionId || null
    });

    // Create promises for all account fetches
    const fetchPromises = connections.map(async (connection) => {
      try {
        const connRec = connection as UnknownRecord;
        const instId = String(connRec['institutionId']);
        const forceRefresh = forceRefreshInstitutionId === instId;
        await (_get() as unknown as { fetchAccountsForInstitution: (id: string, isNav?: boolean, force?: boolean) => Promise<void> })
          .fetchAccountsForInstitution(instId, true, forceRefresh); // isNavigation=true to suppress individual toasts
        return { institutionId: instId, success: true };
      } catch (error) {
        storeLogger.error('multiWealthica2Store', 'batch_fetch_institution_failed', {
          event: EVENTS.BATCH_FETCH_START,
          institutionId: (connection as UnknownRecord)['institutionId'],
          error: error instanceof Error ? error.message : 'Unknown error',
          isConnected: Boolean((connection as UnknownRecord)['isConnected']),
          hasToken: Boolean((connection as UnknownRecord)['bearerToken'])
        });
        return { institutionId: (connection as UnknownRecord)['institutionId'] as string, success: false, error };
      }
    });

    try {
      const results = await Promise.all(fetchPromises);
      const successCount = results.filter(r => r.success).length;

      // Quieter UX: only notify on failures in batch mode (no success toast)
      if (!isNavigation && connections.length > 1) {
        if (results.length === successCount) {
          // All good: no toast
        } else if (successCount > 0) {
          toast.warning(`⚠️ ${successCount} institutions loaded, ${results.length - successCount} failed`);
        } else {
          toast.error('❌ Failed to load accounts for all institutions');
        }
      }
      // For single connection, let individual toast show instead of batch toast
    } finally {
      // Clear batch fetching flag and update timestamp
      _set({
        isFetchingAllConnections: false,
        lastBatchFetch: new Date()
      });
      // Log batch end
      storeLogger.info('multiWealthica2Store', EVENTS.BATCH_FETCH_END, {
        attempted: connections.length
      });
    }
  }
  });
};
